/*
 * SUPERSTAR loyalty page - frontend controller.
 *
 * Two render modes share one bundle. The Liquid section ships
 * `data-state="guest"` or `data-state="auth"` and slightly different
 * DOM; this controller only hydrates what's present.
 *
 * Responsibilities:
 *   1. AUTH hero hydration - animated points counter, tier ladder fill,
 *      progress message, faded tier-skull background.
 *   2. Sticky tab bar - full-width tabs with scroll-spy + a sentinel
 *      that toggles `.is-stuck` on the bar for the transparent/blur skin.
 *   3. Tiers section - 4-tier skull switcher with crossfade.
 *   4. Rewards catalog - branches on `display_kind`:
 *        - `continuous`  → info card, no claim CTA
 *        - `voucher`     → claimable card with inline confirm
 *   5. Your Rewards (auth) - claimed-code grid from /claimed-rewards.
 *   6. Reveal-on-scroll for top-level sections.
 *
 * Phase 1 trust model: X-Customer-Email is sent naked; phase 1.5
 * (Shopify App Proxy HMAC) replaces this before go-live.
 */

(() => {
    'use strict'

    const root = document.querySelector('.superstar')
    if (!root) return

    const workerBase = root.dataset.worker
    const customerEmail = root.dataset.customerEmail || null
    const customerName = root.dataset.customerName || null
    const isAuth = root.dataset.state === 'auth'

    // ----- Tier image map (used by both auth hero bg and tiers section) -----
    const tierImagesEl = root.querySelector('script[data-tier-images]')
    let tierImages = {}
    try { tierImages = JSON.parse(tierImagesEl?.textContent || '{}') } catch (_) {}

    // ----- Tier metadata (slug, name, threshold) from Liquid blocks. -----
    // The block list is the source of truth; we never hardcode tier slugs.
    const tierMetaEl = root.querySelector('script[data-tier-meta]')
    let tierMeta = []
    try { tierMeta = JSON.parse(tierMetaEl?.textContent || '[]') } catch (_) {}
    const TIER_ORDER = tierMeta.map(t => t.slug)

    // ----- i18n: strings pre-translated by Liquid via Shopify's t-filter.
    // Locale is determined by Shopify Markets (PL market -> pl.json).
    // Merchant translates via Shopify admin -> Languages. -----
    const i18nEl = root.querySelector('script[data-i18n]')
    let i18nDict = {}
    try { i18nDict = JSON.parse(i18nEl?.textContent || '{}') } catch (_) {}

    function tr(key, vars) {
        let s = i18nDict[key] || ''
        if (!s) return ''
        if (vars) {
            for (const k of Object.keys(vars)) {
                s = s.split(`{{ ${k} }}`).join(String(vars[k]))
            }
        }
        return s
    }

    // -----------------------------------------------------------------
    // Worker API helpers
    // -----------------------------------------------------------------

    function headers() {
        const h = { 'Accept': 'application/json' }
        if (customerEmail) h['X-Customer-Email'] = customerEmail
        return h
    }
    async function api(path, opts = {}) {
        try {
            const res = await fetch(`${workerBase}${path}`, {
                ...opts,
                headers: { ...headers(), ...(opts.headers || {}) },
            })
            if (!res.ok) return null
            return await res.json()
        } catch (e) {
            console.error('loyalty fetch failed', path, e)
            return null
        }
    }

    // -----------------------------------------------------------------
    // Tier panel (Tiers section)
    //
    // Tiers (slug, name, threshold, benefits) are rendered server-side
    // from section blocks. JS only swaps which benefit panel is visible
    // and updates the skull image when the user picks a tier tab.
    // -----------------------------------------------------------------

    function applyTierDisplay(slug) {
        const display = root.querySelector('[data-tier-display="tiers"]')
        if (!display) return
        display.dataset.tier = slug
        const img = tierImages[slug]
        display.style.setProperty('--skull-image', img ? `url("${img}")` : 'none')
    }

    function showTier(slug) {
        if (!slug || !TIER_ORDER.includes(slug)) return
        applyTierDisplay(slug)

        // Swap benefits panel: only the matching tier-benefits div is shown.
        root.querySelectorAll('[data-tier-benefits]').forEach(div => {
            div.hidden = div.dataset.tierBenefits !== slug
        })

        // Tab active state.
        root.querySelectorAll('[data-tier-tabs] .ss-tier-tab').forEach(btn => {
            const active = btn.dataset.tier === slug
            btn.classList.toggle('is-active', active)
            btn.setAttribute('aria-selected', active ? 'true' : 'false')
        })
        positionTierIndicator()
    }

    function positionTierIndicator() {
        const indicator = root.querySelector('[data-tier-indicator]')
        const active = root.querySelector('[data-tier-tabs] .ss-tier-tab.is-active')
        if (!indicator || !active) return
        const tabsRect = active.parentElement.getBoundingClientRect()
        const r = active.getBoundingClientRect()
        indicator.style.transform = `translateX(${r.left - tabsRect.left}px)`
        indicator.style.width = `${r.width}px`
    }

    function bindTierTabs() {
        root.querySelectorAll('[data-tier-tabs] .ss-tier-tab').forEach(btn => {
            btn.addEventListener('click', () => showTier(btn.dataset.tier))
        })
        window.addEventListener('resize', () => {
            positionTierIndicator()
            positionTabIndicator()
        })
        bindTierSwipe()
    }

    // Mobile horizontal swipe on the tier panel switches to the next /
    // previous tier. Only listens at <=777px to match the rest of the
    // mobile-specific UI; on desktop the click tabs do the work.
    function bindTierSwipe() {
        const panel = root.querySelector('[data-tier-panel], .ss-tiers-panel')
            || root.querySelector('[data-tier-benefits]')?.closest('.ss-tiers-panel, section')
        if (!panel) return
        const SWIPE_MIN = 50      // px horizontal distance to count as a swipe
        const VERTICAL_MAX = 35   // px vertical drift cap (otherwise it's a scroll)
        let startX = 0, startY = 0, tracking = false

        const isMobile = () => window.matchMedia('(max-width: 777px)').matches

        panel.addEventListener('touchstart', (e) => {
            if (!isMobile() || !e.touches[0]) return
            startX = e.touches[0].clientX
            startY = e.touches[0].clientY
            tracking = true
        }, { passive: true })

        panel.addEventListener('touchend', (e) => {
            if (!tracking) return
            tracking = false
            const t = e.changedTouches[0]
            if (!t) return
            const dx = t.clientX - startX
            const dy = t.clientY - startY
            if (Math.abs(dy) > VERTICAL_MAX) return
            if (Math.abs(dx) < SWIPE_MIN) return
            const active = root.querySelector('[data-tier-tabs] .ss-tier-tab.is-active')
            const currentSlug = active?.dataset.tier
            const idx = TIER_ORDER.indexOf(currentSlug)
            if (idx < 0) return
            // Swipe LEFT (dx<0) -> next tier; swipe RIGHT (dx>0) -> prev tier.
            const nextIdx = dx < 0 ? idx + 1 : idx - 1
            if (nextIdx < 0 || nextIdx >= TIER_ORDER.length) return
            showTier(TIER_ORDER[nextIdx])
        }, { passive: true })
    }

    // -----------------------------------------------------------------
    // Sticky tabs - smooth scroll, scroll-spy, stuck detection
    // -----------------------------------------------------------------

    function bindStickyTabs() {
        const tabs = root.querySelectorAll('[data-tabs-bar] .ss-tab')
        if (!tabs.length) return

        tabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                e.preventDefault()
                const id = tab.getAttribute('href').slice(1)
                const target = document.getElementById(id)
                if (!target) return
                const headSpace = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--headSpace')) || 0
                const offset = headSpace + 56 + 12
                const top = target.getBoundingClientRect().top + window.scrollY - offset
                window.scrollTo({ top, behavior: 'smooth' })
            })
        })

        // Scroll-spy: section closest to the top of the visible area
        // (under sticky tabs) gets the active class. Sections are derived
        // from the tab list itself, so guest/auth views work without a
        // hardcoded list.
        const sectionIds = Array.from(tabs).map(t => t.dataset.tab)
        const sections = sectionIds
            .map(id => document.getElementById(id))
            .filter(Boolean)

        const setActive = (id) => {
            tabs.forEach(t => t.classList.toggle('is-active', t.dataset.tab === id))
            positionTabIndicator()
        }

        const io = new IntersectionObserver((entries) => {
            const visible = entries
                .filter(e => e.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
            if (visible[0]) setActive(visible[0].target.id)
        }, {
            rootMargin: '-80px 0px -60% 0px',
            threshold: 0,
        })
        sections.forEach(s => io.observe(s))

        // "Stuck" detection - when the sentinel above the bar leaves
        // the viewport, the bar is pinned. Flip skin to transparent + blur.
        const bar = root.querySelector('[data-tabs-bar]')
        const sentinel = root.querySelector('[data-tabs-sentinel]')
        if (bar && sentinel) {
            const stickIo = new IntersectionObserver(
                ([entry]) => bar.classList.toggle('is-stuck', !entry.isIntersecting),
                { rootMargin: `-${(parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--headSpace')) || 0)}px 0px 0px 0px`, threshold: 0 },
            )
            stickIo.observe(sentinel)
        }
    }

    function positionTabIndicator() {
        const indicator = root.querySelector('[data-tabs-bar] [data-tab-indicator]')
        const active = root.querySelector('[data-tabs-bar] .ss-tab.is-active')
        if (!indicator || !active) return
        const barRect = active.parentElement.getBoundingClientRect()
        const r = active.getBoundingClientRect()
        indicator.style.transform = `translateX(${r.left - barRect.left}px)`
        indicator.style.width = `${r.width}px`
    }

    // -----------------------------------------------------------------
    // Auth hero - points counter + tier ladder + progress message
    // -----------------------------------------------------------------

    function applyAuthHero(me) {
        if (!isAuth || !me?.authenticated || !me.customer) return
        const c = me.customer
        // The tier is whatever LoyaltyLion says it is. When LL doesn't know
        // it, we render no tier at all - a confidently wrong rank is worse
        // than a blank one. Defaulting to 'bronze' here is what put every
        // customer on the wrong rung, with the wrong skull and the wrong
        // benefits, for as long as LL stopped reporting tiers.
        const tierSlug = c.tier?.slug ? String(c.tier.slug).toLowerCase() : null
        const idx = tierSlug ? TIER_ORDER.indexOf(tierSlug) : -1
        const hasTier = idx >= 0

        // Background = current tier skull (faded).
        const bg = root.querySelector('[data-hero-bg]')
        const tierImg = hasTier ? tierImages[tierSlug] : null
        if (bg && tierImg) bg.style.backgroundImage = `url("${tierImg}")`

        // Big counter shows spendable balance (approved). Tier math
        // separately uses `lifetime` (approved + spent) so the ladder
        // doesn't move backwards when the customer redeems a reward.
        const approved = c.points?.approved || 0
        const pending = c.points?.pending || 0
        const lifetime = c.points?.lifetime ?? (approved + (c.points?.spent || 0))
        currentPoints = approved
        const approvedEl = root.querySelector('[data-points-approved]')
        const pendingEl = root.querySelector('[data-points-pending]')
        if (approvedEl) countTo(approvedEl, approved)
        if (pendingEl) pendingEl.textContent = tr('pending_suffix', { count: pending.toLocaleString('pl-PL') })
        updateRewardAffordability()

        // Tier ladder. Mark passed/current stops - but only if we know where
        // the customer actually stands. No tier means no marks at all.
        TIER_ORDER.forEach((slug, i) => {
            const dot = root.querySelector(`.ss-tier-stop[data-stop="${slug}"]`)
            const lblEl = root.querySelector(`[data-stop-label="${slug}"]`)
            if (dot) {
                dot.classList.toggle('is-passed', hasTier && i < idx)
                dot.classList.toggle('is-current', hasTier && i === idx)
            }
            if (lblEl) lblEl.classList.toggle('is-current', hasTier && i === idx)
        })

        // Tier-metric math. Source of truth = `tierMeta` from Liquid blocks
        // (slug + threshold in points). The customer's tier metric value
        // is `points_approved` (pending doesn't count until cleared).
        // Verified vs the live LL widget at /pages/superstar: customer with
        // 5,075 approved + VIP threshold 20,001 -> "Earn 14,926 more points
        // to reach VIP" (rounding diff between our math and LL is +/-1).
        const lastIdx = Math.max(0, TIER_ORDER.length - 1)
        const stops = TIER_ORDER.map((_, i) => lastIdx > 0 ? (i * 100 / lastIdx) : 50)

        const fillEl = root.querySelector('[data-tier-fill]')
        const pin = root.querySelector('[data-tier-pin]')
        const label = root.querySelector('[data-progress-label]')

        if (!hasTier) {
            // LL has no tier for this customer. Leave the ladder unmarked
            // instead of inventing a position on it.
            if (fillEl) fillEl.style.width = '0%'
            if (pin) pin.hidden = true
            if (label) label.textContent = ''
            return
        }

        const nextMeta = tierMeta[idx + 1]
        const tierName = (c.tier?.name || capitalize(tierSlug)).toUpperCase()

        // Which number advances the pin, in order of trust:
        //   1. LL's own tier metric (tier_points) - matches LL admin exactly.
        //   2. our point balance, but ONLY when it is consistent with the tier
        //      LL assigned. A customer placed by import can sit below their own
        //      tier's floor, and measuring a distance from that is exactly what
        //      produced "earn 0 more points to reach Silver".
        const llFloor = typeof c.tier?.lower_bound_points === 'number' ? c.tier.lower_bound_points : null
        const metric = typeof c.tier?.tier_points === 'number'
            ? c.tier.tier_points
            : (llFloor == null || lifetime >= llFloor) ? lifetime : null

        if (nextMeta) {
            const lower = tierMeta[idx]?.threshold || 0
            const upper = nextMeta.threshold
            const inTier = (metric != null && upper > lower)
                ? Math.max(0, Math.min(1, (metric - lower) / (upper - lower)))
                : 0
            const pinPct = stops[idx] + inTier * (stops[idx + 1] - stops[idx])
            if (fillEl) fillEl.style.width = `${pinPct}%`
            if (pin) {
                pin.style.left = `${pinPct}%`
                pin.hidden = false
            }
            if (label) {
                // Prefer LL's own figure - it matches the LL admin panel. Fall
                // back to the ladder only when we have a metric we trust, and
                // otherwise state where the customer is without promising a
                // distance we can't compute.
                const togo = typeof c.tier?.points_to_next_tier === 'number'
                    ? c.tier.points_to_next_tier
                    : (metric != null ? Math.max(0, upper - metric) : null)
                const nextName = c.tier?.next_tier?.name ?? nextMeta.name
                label.textContent = togo == null
                    ? tr('progress_current', { tier: tierName })
                    : tr('progress_next', {
                        togo: togo.toLocaleString('pl-PL'),
                        tier: nextName,
                    })
            }
        } else {
            // Top tier reached - full fill, pin at far right, "top tier" copy.
            if (fillEl) fillEl.style.width = '100%'
            if (pin) {
                pin.style.left = '100%'
                pin.hidden = false
            }
            if (label) label.textContent = tr('progress_top', { tier: tierName })
        }
    }

    function countTo(el, target) {
        const dur = 900
        const t0 = performance.now()
        function step(now) {
            const p = Math.min(1, (now - t0) / dur)
            const eased = 1 - Math.pow(1 - p, 3)
            el.textContent = Math.round(target * eased).toLocaleString('pl-PL')
            if (p < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
    }

    // -----------------------------------------------------------------
    // Rewards catalog - branch on display_kind
    // -----------------------------------------------------------------

    // Cached after /me responds so renderRewards can decide affordability.
    let currentPoints = 0

    function renderRewards(rewards) {
        const grid = root.querySelector('[data-rewards-grid]')
        if (!grid) return
        if (!rewards || !rewards.length) {
            // On the guest preview the section's subheading already
            // explains the state ("A preview of what SUPERSTAR members
            // can claim..."), and there's a JOIN button below. Showing
            // a second "no rewards available" line on top is redundant
            // and reads as a broken empty state, so we clear the grid
            // instead. Auth view keeps the empty message because that
            // IS the actionable state (nothing to spend points on yet).
            if (!isAuth) {
                grid.innerHTML = ''
            } else {
                grid.innerHTML = `<p class="ss-empty">${escapeHtml(tr('rewards_empty'))}</p>`
            }
            return
        }

        grid.innerHTML = rewards.map(r => {
            if (r.display_kind === 'continuous') return renderContinuousCard(r)
            return renderVoucherCard(r)
        }).join('')

        bindVoucherCards(grid)
    }

    function updateRewardAffordability() {
        // Toggle .ss-reward[data-affordable] + claim button copy based on
        // the current points balance. Called once after /me hydrates and
        // again after every successful claim.
        root.querySelectorAll('.ss-reward[data-cost]').forEach(card => {
            const cost = Number(card.dataset.cost) || 0
            const affordable = currentPoints >= cost
            card.dataset.affordable = affordable ? 'true' : 'false'
            const claim = card.querySelector('[data-claim]')
            if (claim) {
                if (!affordable && isAuth) {
                    const need = (cost - currentPoints).toLocaleString('pl-PL')
                    claim.disabled = true
                    claim.textContent = tr('need_more', { amount: need })
                    claim.title = tr('affordability_tooltip', {
                        have: currentPoints.toLocaleString('pl-PL'),
                        cost: cost.toLocaleString('pl-PL'),
                    })
                } else if (isAuth) {
                    claim.disabled = false
                    claim.textContent = tr('claim')
                    claim.removeAttribute('title')
                }
            }
        })
    }

    function renderContinuousCard(r) {
        // Ratio shown in PLN regardless of market - LL stores it as PLN
        // and Shopify converts foreign-market checkout totals to PLN
        // before LL applies the rate.
        // LL Headless gives us already-formatted text like
        // "10 points per PLN 1". Fall back to the old template if missing.
        const ratio = r.cost_text
            || (r.discount_amount && r.cost
                ? tr('continuous_ratio', { pts: r.cost, amount: r.discount_amount })
                : '')
        return `
            <article class="ss-reward ss-reward--info" data-reward-id="${r.id}">
                <h3 class="ss-reward__title">${escapeHtml(tr('continuous_title'))}</h3>
                <p class="ss-reward__ratio">${escapeHtml(ratio)}</p>
                <p class="ss-reward__hint">${escapeHtml(tr('continuous_hint'))}</p>
            </article>
        `
    }

    function renderVoucherCard(r) {
        const amountText = r.discount_amount
            ? (r.discount_type === 'percentage' ? `${r.discount_amount}%` : `${r.discount_amount} PLN`)
            : escapeHtml(r.title)
        const iconUrl = root.dataset.rewardIcon
            || 'https://cdn.shopify.com/s/files/1/0764/9381/6153/files/icon_Obszar_roboczy_1_kopia.png?v=1745331762'
        return `
            <article class="ss-reward" data-reward-id="${r.id}" data-cost="${r.cost}">
                <div class="ss-reward__icon" aria-hidden="true">
                    <img src="${escapeHtml(iconUrl)}" alt="" loading="lazy">
                </div>
                <div class="ss-reward__details">
                    <h3 class="ss-reward__title">${escapeHtml(r.title)}</h3>
                    <span class="ss-reward__amount">${escapeHtml(amountText)}<span>${escapeHtml(tr('off_label'))}</span></span>
                    <span class="ss-reward__cost">${r.cost.toLocaleString('pl-PL')} pts</span>
                </div>
                ${isAuth
                    ? `<button type="button" class="ss-reward__claim" data-claim>${escapeHtml(tr('claim'))}</button>`
                    : `<a href="/account/login?return_to=${encodeURIComponent(location.pathname)}" class="ss-reward__claim is-ghost">${escapeHtml(tr('signin_to_claim'))}</a>`}
                <div class="ss-reward__expand" hidden>
                    <p>${escapeHtml(tr('confirm_q', { pts: r.cost.toLocaleString('pl-PL'), amount: amountText }))}</p>
                    <div class="ss-reward__buttons">
                        <button type="button" class="ss-reward__confirm-btn" data-confirm>${escapeHtml(tr('confirm'))}</button>
                        <button type="button" class="ss-reward__cancel" data-cancel>${escapeHtml(tr('cancel'))}</button>
                    </div>
                </div>
                <div class="ss-reward__result" hidden></div>
            </article>
        `
    }

    function bindVoucherCards(grid) {
        grid.querySelectorAll('.ss-reward:not(.ss-reward--info)').forEach(card => {
            const claim = card.querySelector('[data-claim]')
            const expand = card.querySelector('.ss-reward__expand')
            const confirmBtn = card.querySelector('[data-confirm]')
            const cancel = card.querySelector('[data-cancel]')
            const result = card.querySelector('.ss-reward__result')
            if (!claim) return

            claim.addEventListener('click', () => {
                expand.hidden = false
                claim.disabled = true
            })
            cancel?.addEventListener('click', () => {
                expand.hidden = true
                claim.disabled = false
            })
            confirmBtn?.addEventListener('click', async () => {
                confirmBtn.disabled = true
                confirmBtn.textContent = tr('claiming')
                const id = Number(card.dataset.rewardId)
                const res = await api('/api/loyalty/redeem', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reward_id: id }),
                })
                expand.hidden = true
                result.hidden = false
                if (res?.ok && res.code) {
                    result.innerHTML = `
                        <p>${escapeHtml(tr('done'))}</p>
                        <div class="ss-reward__code">
                            <code>${escapeHtml(res.code)}</code>
                            <button type="button" class="ss-copy" data-copy="${escapeHtml(res.code)}">${escapeHtml(tr('copy'))}</button>
                        </div>
                    `
                    refresh()
                } else {
                    result.innerHTML = `<p>${escapeHtml(tr('claim_failed'))} ${escapeHtml(res?.state || '')}</p>`
                    confirmBtn.disabled = false
                    confirmBtn.textContent = tr('confirm')
                    claim.disabled = false
                }
            })
        })
    }

    // -----------------------------------------------------------------
    // Your Rewards (auth) - claimed codes
    // -----------------------------------------------------------------

    function renderClaimed(items) {
        const grid = root.querySelector('[data-claimed-grid]')
        if (!grid) return
        if (!items || !items.length) {
            grid.innerHTML = `
                <div class="ss-claimed-empty">
                    <strong>${escapeHtml(tr('your_rewards_empty_title'))}</strong>
                    ${escapeHtml(tr('your_rewards_empty_body'))}
                </div>
            `
            return
        }
        grid.innerHTML = items.map(c => {
            const amount = c.discount_amount
                ? `${c.discount_amount} zł`
                : (c.title || '')
            const claimedLine = tr('your_rewards_claimed_on', { date: formatDate(c.claimed_at) })
            const expiresLine = c.expires_on
                ? ` · ${tr('your_rewards_expires_on', { date: formatDate(c.expires_on) })}`
                : ''
            return `
                <article class="ss-claimed" data-claimed-id="${c.id}">
                    <header class="ss-claimed__head">
                        <h3 class="ss-claimed__title">${escapeHtml(c.title || amount)}</h3>
                        <span class="ss-claimed__cost">${c.point_cost ? c.point_cost.toLocaleString('pl-PL') + ' pts' : ''}</span>
                    </header>
                    ${c.code ? `
                        <div class="ss-claimed__code">
                            <code>${escapeHtml(c.code)}</code>
                            <button type="button" class="ss-copy" data-copy="${escapeHtml(c.code)}">${escapeHtml(tr('copy'))}</button>
                        </div>
                    ` : ''}
                    <span class="ss-claimed__meta">${escapeHtml(claimedLine)}${escapeHtml(expiresLine)}</span>
                </article>
            `
        }).join('')
    }

    function formatDate(iso) {
        if (!iso) return ''
        try {
            return new Date(iso).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' })
        } catch (_) { return '' }
    }

    // -----------------------------------------------------------------
    // Activity feed (auth) - LL activities mapped to readable rows
    // -----------------------------------------------------------------

    function renderActivity(items) {
        const list = root.querySelector('[data-activity-list]')
        if (!list) return
        if (!items || !items.length) {
            list.innerHTML = `<div class="ss-activity-empty">${escapeHtml(tr('activity_empty'))}</div>`
            return
        }
        list.innerHTML = items.map(a => {
            const pts = a.points || 0
            const sign = pts > 0 ? '+' : (pts < 0 ? '' : '')
            const cls = pts > 0 ? 'is-gain' : (pts < 0 ? 'is-loss' : '')
            return `
                <div class="ss-activity" data-activity-id="${a.id}">
                    <span class="ss-activity__title">${escapeHtml(a.title)}</span>
                    <span class="ss-activity__date">${formatDate(a.created_at)}</span>
                    <span class="ss-activity__points ${cls}">${sign}${pts.toLocaleString('pl-PL')} pts</span>
                </div>
            `
        }).join('')
    }

    // -----------------------------------------------------------------
    // Personal referral link injection
    // -----------------------------------------------------------------

    function applyReferralLink(me) {
        const url = me?.customer?.referral_url
        if (!url) return
        const cta = root.querySelector('.ss-level-card__cta[data-kind="referral"]')
        if (cta) cta.href = url
    }

    // -----------------------------------------------------------------
    // LL SDK action bridges
    //
    // The Level Up CTAs (Add birthday, Follow on Instagram, Subscribe to
    // newsletter, Refer a friend, etc.) only AWARD POINTS when the LL
    // SDK's own modal/flow runs - clicking a plain href to instagram.com
    // does nothing. The SDK is loaded globally via the LoyaltyLion theme
    // app embed, so we hook into `loyaltylion.ui.openRuleActionModal` and
    // `loyaltylion.ui.openReferralWidgetModal` from our buttons.
    //
    // Rule IDs are read at click time from
    //   loyaltylion.store.getState().program.rules
    // matched by `kind`, so we don't have to hardcode numeric IDs (they
    // can change if the merchant rewires rules in the LL admin).
    // -----------------------------------------------------------------

    const LL_KIND_BY_DATA_KIND = {
        instagram:  'instagramFollow',
        tiktok:     'tiktokFollow',
        newsletter: 'newsletterSignup',
        birthday:   'birthday',
        referral:   'referral',
        signup:     'signup',
        purchase:   'purchase',
    }

    function findLLRule(dataKind) {
        const llKind = LL_KIND_BY_DATA_KIND[dataKind]
        if (!llKind) return null
        const state = window.loyaltylion?.store?.getState?.()
        const rules = state?.program?.rules
        if (!Array.isArray(rules)) return null
        return rules.find(r => r.kind === llKind) || null
    }

    function bindLLActionButtons() {
        // Delegated click on any Level Up CTA. Falls through to the
        // anchor's default href if the SDK can't handle it (offline,
        // logout, etc.).
        root.addEventListener('click', (e) => {
            const cta = e.target.closest('.ss-level-card__cta[data-kind]')
            if (!cta) return
            // Already-completed CTAs render as a span.is-done; the listener
            // doesn't see them because they aren't anchors. But guard
            // anyway in case schema changes.
            if (cta.classList.contains('is-done')) {
                e.preventDefault()
                return
            }
            const kind = cta.dataset.kind
            const ll = window.loyaltylion

            if (kind === 'referral') {
                if (ll?.ui?.openReferralWidgetModal) {
                    e.preventDefault()
                    ll.ui.openReferralWidgetModal()
                }
                return
            }

            const rule = findLLRule(kind)
            if (rule && ll?.ui?.openRuleActionModal) {
                e.preventDefault()
                ll.ui.openRuleActionModal(rule.id)
            }
        })
    }

    /**
     * Mark Level Up CTAs as "Completed" for rules the customer has
     * already finished. The SDK's `customer.completedRules` array
     * carries the canonical truth; we mirror it onto our card CTAs so
     * users don't re-click an Instagram follow that's already credited.
     */
    function markCompletedCTAs() {
        const state = window.loyaltylion?.store?.getState?.()
        const completed = state?.customer?.completedRules
        if (!Array.isArray(completed) || !completed.length) return
        const rules = state.program?.rules || []
        const completedIds = new Set(completed.map(c => c.ruleId))

        for (const [dataKind, llKind] of Object.entries(LL_KIND_BY_DATA_KIND)) {
            const rule = rules.find(r => r.kind === llKind)
            if (!rule || !completedIds.has(rule.id)) continue
            // Skip purchase - it's not a one-time rule, the badge would be
            // misleading.
            if (dataKind === 'purchase') continue
            const cta = root.querySelector(`.ss-level-card__cta[data-kind="${dataKind}"]`)
            if (!cta || cta.classList.contains('is-done')) continue
            const label = tr('cta_completed') || cta.textContent.trim()
            const span = document.createElement('span')
            span.className = 'ss-level-card__cta is-done'
            span.dataset.kind = dataKind
            span.textContent = label
            cta.replaceWith(span)
        }
    }

    /**
     * Watch the LL SDK store for state changes. When a rule completes or
     * the customer's points balance changes, refresh our hero/activity
     * sections so the UI reflects the new state without a page reload.
     */
    function bindLLStoreSync() {
        const ll = window.loyaltylion
        if (!ll?.store?.subscribe || !ll?.store?.getState) return
        let lastApproved = ll.store.getState().customer?.pointsApproved ?? 0
        let lastCompleted = ll.store.getState().customer?.completedRules?.length ?? 0
        ll.store.subscribe(() => {
            const s = ll.store.getState()
            const approved = s.customer?.pointsApproved ?? 0
            const completedCount = s.customer?.completedRules?.length ?? 0
            if (approved !== lastApproved || completedCount !== lastCompleted) {
                lastApproved = approved
                lastCompleted = completedCount
                // Bust Worker cache and re-pull. /redeem isn't the only
                // way points change anymore - LL action completions also
                // need our UI to refresh.
                refresh().catch(() => {})
                markCompletedCTAs()
            }
        })
    }

    // -----------------------------------------------------------------
    // Reveal-on-scroll
    // -----------------------------------------------------------------

    function bindReveal() {
        const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible')
                    io.unobserve(entry.target)
                }
            })
        }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })

        root.querySelectorAll('.ss-reveal').forEach(el => io.observe(el))
        const hero = root.querySelector('.ss-hero')
        if (hero) requestAnimationFrame(() => hero.classList.add('is-visible'))
    }

    // -----------------------------------------------------------------
    // Copy-to-clipboard (delegated)
    // -----------------------------------------------------------------

    root.addEventListener('click', async (e) => {
        const target = e.target.closest('[data-copy]')
        if (!target) return
        const code = target.dataset.copy
        try {
            await navigator.clipboard.writeText(code)
            const orig = target.textContent
            target.textContent = tr('copied') || 'Copied!'
            setTimeout(() => { target.textContent = orig }, 1200)
        } catch (_) {}
    })

    // -----------------------------------------------------------------
    // Util
    // -----------------------------------------------------------------

    function escapeHtml(s) {
        return String(s ?? '').replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]))
    }
    function capitalize(s) {
        if (!s) return s
        // VIP stays all-caps as an acronym, not "Vip".
        if (s.toLowerCase() === 'vip') return 'VIP'
        return s[0].toUpperCase() + s.slice(1)
    }

    // -----------------------------------------------------------------
    // Boot
    // -----------------------------------------------------------------

    async function boot() {
        bindReveal()
        bindTierTabs()
        bindStickyTabs()
        bindLLActionButtons()
        // The SDK might not be ready at script start. Try now and on
        // window-load as a fallback so the store-sync still attaches.
        if (window.loyaltylion?.store?.subscribe) {
            bindLLStoreSync()
            markCompletedCTAs()
        } else {
            window.addEventListener('load', () => {
                bindLLStoreSync()
                markCompletedCTAs()
            }, { once: true })
        }

        // Initialise the Tiers section with the lowest tier (first block).
        // Tier data comes from Liquid blocks; no Worker fetch needed.
        if (TIER_ORDER.length) showTier(TIER_ORDER[0])

        if (isAuth) {
            const [meResp, rewardsResp, claimedResp, activityResp] = await Promise.all([
                api('/api/loyalty/me'),
                api('/api/loyalty/rewards'),
                api('/api/loyalty/claimed-rewards'),
                api('/api/loyalty/activity'),
            ])
            renderRewards(rewardsResp?.rewards || [])
            renderClaimed(claimedResp?.claimed_rewards || [])
            renderActivity(activityResp?.activity || [])
            if (meResp?.authenticated && meResp.customer) {
                applyAuthHero(meResp)
                applyReferralLink(meResp)
                const slug = (meResp.customer.tier?.slug || TIER_ORDER[0] || '').toLowerCase()
                if (TIER_ORDER.includes(slug)) showTier(slug)
            }
        } else if (root.querySelector('[data-rewards-grid]')) {
            // Guest view - fetch rewards from the public catalog so the
            // showcase has real data (cards render in read-only mode).
            const rewardsResp = await api('/api/loyalty/rewards')
            renderRewards(rewardsResp?.rewards || [])
        }

        requestAnimationFrame(() => {
            positionTierIndicator()
            positionTabIndicator()
        })
    }

    async function refresh() {
        if (!isAuth) return
        // Deliberately NOT re-rendering the rewards catalog grid - the
        // just-claimed card has the discount code rendered inline, and a
        // full grid rewrite would wipe it before the user copies it.
        // applyAuthHero() updates the points counter and re-runs
        // affordability on the existing cards, which is what we need
        // after a redeem.
        const [meResp, claimedResp, activityResp] = await Promise.all([
            api('/api/loyalty/me'),
            api('/api/loyalty/claimed-rewards'),
            api('/api/loyalty/activity'),
        ])
        if (meResp?.authenticated) {
            applyAuthHero(meResp)
            applyReferralLink(meResp)
        }
        renderClaimed(claimedResp?.claimed_rewards || [])
        renderActivity(activityResp?.activity || [])
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot)
    } else {
        boot()
    }
})()
